import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { holidaysApi, usersApi, leavePoliciesApi } from '../../services/api';
import useStore from '../../store';
import './HolidaysPage.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function HolidaysPage() {
  const { user } = useStore();
  const isAdmin = user?.role === 'admin';
  const year = new Date().getFullYear();

  const [holidays, setHolidays] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', type: 'common', region_id: '', country_id: '', description: '', is_optional: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const params = { year };
    if (!isAdmin) {
      if (user?.country_id) params.country_id = user.country_id;
      if (user?.region_id) params.region_id = user.region_id;
    }

    const [holRes, regRes, countRes] = await Promise.all([
      holidaysApi.list(params),
      usersApi.regions(),
      usersApi.countries(),
    ]);
    setHolidays(holRes.data.data);
    setRegions(regRes.data.data);
    setCountries(countRes.data.data);
    // Load user's assigned leave policy (if any) and show its holidays/balances
    try {
      if (user?.leave_policy_id) {
        const pol = await leavePoliciesApi.get(user.leave_policy_id);
        setPolicy(pol.data.data);
      } else {
        setPolicy(null);
      }
    } catch (err) {
      // ignore policy fetch errors, fallback to global holidays
      setPolicy(null);
    }
  }, [year, isAdmin, user?.country_id, user?.region_id, user?.leave_policy_id]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await holidaysApi.create({ ...form, region_id: form.region_id || null, country_id: form.country_id || null });
      toast.success('Holiday added!');
      setShowCreate(false);
      setForm({ name: '', date: '', type: 'common', region_id: '', country_id: '', description: '', is_optional: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await holidaysApi.delete(id);
      toast.success('Holiday removed');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const isPolicyMode = !!user?.leave_policy_id && (!isAdmin || filterType === 'my_holidays');
  const sourceHolidays = isPolicyMode ? (policy?.holidays || []) : holidays;
  console.log('Holidays to show', sourceHolidays);

  // Group holidays by month
  const filtered = sourceHolidays.filter((h) => {
    let matchType = true;
    let matchCountry = true;
    let matchRegion = true;

    if (filterType !== 'all') {
      if (filterType === 'my_holidays' && isPolicyMode) {
        matchType = true;
      } else if (filterType === 'my_holidays') {
        const userCountry = user?.country_id;
        const userRegion = user?.region_id;
        matchType = (h.type === 'common' && h.country_id === userCountry) || (h.type === 'regional' && h.region_id === userRegion);
      } else {
        matchType = h.type === filterType;
      }
    }

    if (filterCountry && !(filterType === 'my_holidays' && isPolicyMode)) {
      matchCountry = (h.country_id === filterCountry);
    }
    if (filterRegion && h.type === 'regional' && !(filterType === 'my_holidays' && isPolicyMode)) {
      matchRegion = (h.region_id === filterRegion);
    }

    return matchType && matchCountry && matchRegion;
  });

  const grouped = MONTHS.reduce((acc, _, idx) => {
    const monthHols = filtered.filter((h) => new Date(h.date).getMonth() === idx);
    if (monthHols.length) acc[idx] = monthHols;
    return acc;
  }, {});

  const today = new Date().toISOString().split('T')[0];
  const upcoming = sourceHolidays.find((h) => h.date >= today);

  return (
    <div className="holidays-page">
      <div className="page-header">
        <div>
          <div className="page-title">Holidays {year}</div>
          <div className="page-subtitle">{isPolicyMode ? 'Holidays from your assigned leave policy' : 'Company-wide and regional holidays calendar'}</div>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Add Holiday</button>
        )}
      </div>

      {/* Next holiday banner */}
      {upcoming && (
        <div className="next-holiday-banner animate-in">
          <div className="next-holiday-left">
            <span className="next-holiday-tag">Next Holiday</span>
            <h3>{upcoming.name}</h3>
            <p>{new Date(upcoming.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
          <div className="next-holiday-countdown">
            <span className="countdown-num">
              {Math.ceil((new Date(upcoming.date) - new Date()) / (1000 * 60 * 60 * 24))}
            </span>
            <span className="countdown-label">days away</span>
          </div>
        </div>
      )}
      {/* Filter */}
      <div className="holidays-filter animate-in stagger-1" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {(isAdmin ? ['all', 'my_holidays', 'common', 'regional'] : ['all', 'common', 'regional']).map((f) => (
            <button
              key={f}
              className={`filter-pill ${filterType === f ? 'filter-pill--active' : ''}`}
              onClick={() => setFilterType(f)}
            >
              {f === 'all' ? '🗓 All' : f === 'my_holidays' ? '👤 My Holidays' : f === 'common' ? '🇮🇳 National' : '📍 Regional'}
              <span className="filter-pill-count" style={{ marginLeft: 6 }}>
                {f === 'all' 
                  ? sourceHolidays.length 
                  : f === 'my_holidays'
                    ? sourceHolidays.filter(h => (h.type === 'common' && h.country_id === user?.country_id) || (h.type === 'regional' && h.region_id === user?.region_id)).length
                    : sourceHolidays.filter(h => h.type === f).length}
              </span>
            </button>
          ))}
        </div>
        
        {isAdmin && filterType !== 'my_holidays' && (
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <select className="form-select" style={{ minWidth: 150 }} value={filterCountry} onChange={(e) => { setFilterCountry(e.target.value); setFilterRegion(''); }}>
              <option value="">All Countries</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select className="form-select" style={{ minWidth: 150 }} value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)} disabled={!filterCountry && filterType !== 'regional'}>
              <option value="">All Regions</option>
              {regions.filter(r => !filterCountry || r.country_id === filterCountry).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Month groups */}
      {Object.keys(grouped).length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">
            {isPolicyMode ? 'No holidays found in your assigned policy' : 'No holidays found'}
          </div>
          <div className="empty-state-text">
            {isPolicyMode ? 'Please contact admin to update your leave policy holidays.' : 'Try changing filters.'}
          </div>
        </div>
      ) : (
      <div className="holidays-grid animate-in stagger-2">
        {Object.entries(grouped).map(([monthIdx, monthHols]) => (
          <div key={monthIdx} className="month-group card">
            <div className="month-header">
              <span className="month-name">{MONTHS[parseInt(monthIdx)]}</span>
              <span className="month-count">{monthHols.length}</span>
            </div>
            <ul className="month-holidays">
              {monthHols.map((h) => {
                const d = new Date(h.date);
                const isPast = h.date < today;
                return (
                  <li key={h.id} className={`month-holiday-item ${isPast ? 'month-holiday-item--past' : ''}`}>
                    <div className="holiday-day-badge">
                      <span>{d.getDate()}</span>
                      <span style={{ fontSize: '0.6rem' }}>{d.toLocaleDateString('en-IN', { weekday: 'short' })}</span>
                    </div>
                    <div className="holiday-detail">
                      <span className="holiday-item-name">{h.name}</span>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                        <span className={`badge ${h.type === 'common' ? 'badge-violet' : 'badge-sky'}`} style={{ fontSize: '0.65rem' }}>
                          {h.type === 'common' ? 'National' : h.region?.name || h.country?.name || 'Regional/Country specific'}
                        </span>
                        {h.is_optional && <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>Optional</span>}
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--accent-rose)', marginLeft: 'auto', flexShrink: 0 }}
                        onClick={() => handleDelete(h.id)}
                      >
                        ✕
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Holiday</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Holiday Name *</label>
                  <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Republic Day" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Date *</label>
                    <input type="date" className="form-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type *</label>
                    <select className="form-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, region_id: '', country_id: '' })}>
                      <option value="common">National / Common</option>
                      <option value="regional">Regional / State specific</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Country <span style={{ color: 'red' }}>*</span></label>
                  <select className="form-select" required value={form.country_id} onChange={(e) => setForm({ ...form, country_id: e.target.value })}>
                    <option value="">— Select Country —</option>
                    {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {form.type === 'regional' && (
                  <div className="form-group">
                    <label className="form-label">Region <span style={{ color: 'red' }}>*</span></label>
                    <select className="form-select" required value={form.region_id} onChange={(e) => setForm({ ...form, region_id: e.target.value })}>
                      <option value="">— Select Region —</option>
                      {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input className="form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description" />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={form.is_optional} onChange={(e) => setForm({ ...form, is_optional: e.target.checked })} />
                  Optional Holiday
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add Holiday'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
